import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApiCaller } from '../utils/ApiUtilities';
import { useUser } from '../contexts/UserContext';
import { useError } from './ErrorContext';
import {
    getMySubscriptionUrl,
    getPlansUrl,
    getPurchaseQuoteUrl,
    getCancelSubscriptionUrl,
} from '../utils/UrlUtilities';
import { PLAN_CODES, PLAN_LABELS } from '../utils/Constants';
import { formatUsdBdt } from '../utils/BasicUtilities';
import { formatBdPst } from '../utils/DateUtilities';

const PREMIUM_TIER_GRADIENT = 'linear-gradient(145deg,#7c3aed,#5b21b6)';

export default function ManageSubscription() {
    const CallApi = useApiCaller();
    const navigate = useNavigate();
    const { userState, refreshSubscription } = useUser();
    const { showSuccess } = useError();

    const [sub, setSub] = useState(null);
    const [plans, setPlans] = useState([]);
    const [quotes, setQuotes] = useState({}); // plan_code -> quote
    const [loading, setLoading] = useState(true);
    const [cancelling, setCancelling] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        const [subRes, plansRes] = await Promise.all([
            CallApi({ url: getMySubscriptionUrl(), method: 'GET', token: true }),
            CallApi({ url: getPlansUrl(), method: 'GET', token: false }),
        ]);
        if (subRes.success) setSub(subRes.data);
        const planList = plansRes.success ? (plansRes.data?.plans || []) : [];
        setPlans(planList);
        // Fetch a quote per plan so we can show "Upgrade for ৳X" / disable downgrades.
        const quoteResults = await Promise.all(
            planList.map(p =>
                CallApi({ url: getPurchaseQuoteUrl({ plan_code: p.code }), method: 'GET', token: true })
            )
        );
        const qmap = {};
        planList.forEach((p, i) => {
            if (quoteResults[i]?.success) qmap[p.code] = quoteResults[i].data;
        });
        setQuotes(qmap);
        setLoading(false);
    }, [CallApi]);

    useEffect(() => { load(); }, [load]);

    const handleCancel = async () => {
        if (!window.confirm('Cancel your subscription? You keep access until it expires, but it will not renew.')) return;
        setCancelling(true);
        const res = await CallApi({ url: getCancelSubscriptionUrl(), method: 'POST', token: true });
        setCancelling(false);
        if (res.success) {
            showSuccess('Subscription cancelled. You keep access until it expires.');
            refreshSubscription();
            load();
        }
    };

    if (!userState?.isLoggedIn) {
        return <div className="container py-5 text-center text-muted">Please log in to manage your subscription.</div>;
    }
    if (loading) {
        return (
            <div className="container py-5 text-center">
                <div className="spinner-border text-primary" role="status" />
            </div>
        );
    }

    const currentCode = sub?.current_plan_code || PLAN_CODES.FREE;
    const isFree = currentCode === PLAN_CODES.FREE;
    const canCancel = !isFree && (sub?.status === 'ACTIVE' || sub?.status === 'TRIAL');

    const actionLabel = (q) => {
        if (!q) return 'Select';
        if (q.mode === 'UPGRADE') return `Upgrade for ${formatUsdBdt(q.final_price_usd, q.final_price_bdt)}`;
        if (q.mode === 'RENEW') return 'Renew for later';
        if (q.mode === 'DOWNGRADE') return 'Buy for later';
        return 'Get this plan';
    };

    return (
        <div className="container py-4">
            <h2 className="fw-bold mb-4" style={{ color: '#4c1d95' }}>Manage Subscription</h2>

            {/* Current plan */}
            <div className="card mb-4" style={{ border: '1px solid #ede9fe', borderRadius: '0.75rem' }}>
                <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                        <div>
                            <div className="text-muted small">Current plan</div>
                            <h4 className="mb-1" style={{ color: '#4c1d95' }}>{PLAN_LABELS[currentCode] || 'Free'}</h4>
                            {isFree ? (
                                <div className="text-muted">You're on the Free plan.</div>
                            ) : (
                                <>
                                    <div>
                                        Current Plan Status: <span className="badge bg-secondary">{sub.status}</span>
                                        {sub.days_remaining > 0 && (
                                            <span className="ms-2">{sub.days_remaining} day{sub.days_remaining === 1 ? '' : 's'} remaining</span>
                                        )}
                                    </div>
                                    {(sub.current_starts_at || sub.expires_at) && (
                                        <div className="text-muted small mt-1" style={{ color: '#166534' }}>
                                            {sub.current_starts_at && <>Started {formatBdPst(sub.current_starts_at, { withTime: false })} · </>}
                                            {sub.expires_at && <>Expires {formatBdPst(sub.expires_at)}</>}
                                        </div>
                                    )}
                                    {sub.status === 'CANCELLED' && (
                                        <div className="text-warning small mt-1">Cancelled — will not renew; access continues until expiry.</div>
                                    )}
                                </>
                            )}
                        </div>
                        {canCancel && (
                            <button className="btn btn-outline-danger btn-sm" onClick={handleCancel} disabled={cancelling}>
                                {cancelling ? 'Cancelling…' : 'Cancel subscription'}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Upcoming (queued) plans */}
            {Array.isArray(sub?.upcoming) && sub.upcoming.length > 0 && (
                <div className="card mb-4" style={{ border: '1px solid #bbf7d0', borderRadius: '0.75rem', background: '#f0fdf4' }}>
                    <div className="card-body">
                        <div className="fw-semibold mb-2" style={{ color: '#15803d' }}>Upcoming plans</div>
                        {sub.upcoming.map((u, i) => (
                            <div key={i} className="d-flex justify-content-between small py-1" style={{ color: '#166534' }}>
                                <span className="fw-semibold">{PLAN_LABELS[u.plan_code] || u.plan_code}</span>
                                <span>{formatBdPst(u.starts_at, { withTime: false })} → {formatBdPst(u.expires_at, { withTime: false })}</span>
                            </div>
                        ))}
                        <div className="text-muted small mt-1">These start automatically when your current plan ends.</div>
                    </div>
                </div>
            )}

            {/* Other plans */}
            <h5 className="mb-3">Plans</h5>
            <div className="row justify-content-center">
                {plans.map(plan => {
                    const q = quotes[plan.code];
                    const isCurrent = plan.code === currentCode;
                    return (
                        <div key={plan.code} className="col-md-4 mb-4">
                            <div className="card h-100" style={{ border: isCurrent ? '2px solid #7c3aed' : '1px solid #ede9fe', borderRadius: '0.75rem', overflow: 'hidden' }}>
                                <div className="text-center" style={{ background: PREMIUM_TIER_GRADIENT, color: '#fff', padding: '0.85rem' }}>
                                    <h3 style={{ fontSize: '1.1rem', fontWeight: 700, margin: 0 }}>{plan.name}</h3>
                                </div>
                                <div className="card-body d-flex flex-column">
                                    <div className="text-center mb-2">
                                        <span style={{ fontSize: '1.6rem', fontWeight: 800, color: '#4c1d95' }}>{formatUsdBdt(plan.price_usd, plan.price_bdt)}</span>
                                        <span className="text-muted"> / {plan.duration_days} days</span>
                                    </div>
                                    <ul className="list-unstyled flex-grow-1" style={{ fontSize: '0.85rem' }}>
                                        {(plan.features || []).map((b, i) => (
                                            <li key={i} className="mb-2"><i className="bi bi-check-circle-fill text-success me-2" />{b}</li>
                                        ))}
                                    </ul>
                                    {isCurrent && <div className="badge bg-light text-dark mb-2 align-self-center">Your current plan</div>}
                                    {q?.allowed === false ? (
                                        <>
                                            <div className="text-center small text-muted mb-1">{q.reason}</div>
                                            <button className="btn btn-sm w-100 btn-outline-secondary" disabled title={q.reason}>
                                                Not available
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            {(q?.mode === 'RENEW' || q?.mode === 'DOWNGRADE') && (
                                                <div className="text-center small text-muted mb-1">Starts after your current plan ends</div>
                                            )}
                                            <button
                                                className="btn btn-sm w-100"
                                                style={{ background: PREMIUM_TIER_GRADIENT, color: '#fff', fontWeight: 600 }}
                                                onClick={() => navigate(`/subscription/${plan.code}`)}
                                            >
                                                {actionLabel(q)}
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
